/**
 * Showing that something is happening, when the thing that is happening is slow.
 *
 * THE OPERATOR'S COMPLAINT, TWICE, IN TWO FORMS. First: "the turns are not
 * moving at all", against a rail reading `0/40` next to a worker that was
 * genuinely working. Then, directly: "we need to visualize things that are
 * being worked on, either by using pulse animations etc. we need to let the
 * user know that things are happening."
 *
 * He was right both times and the counter was not wrong. An agent turn is a
 * model call plus a beacon dispatch: one to three minutes. So `turns_used`
 * changes once every couple of minutes and is `0` for the whole of the first
 * turn, which is precisely the window where an operator is deciding whether to
 * trust the thing. A number that is correct and still means nothing is the same
 * defect as the frozen confidence percentage, one layer over.
 *
 * TWO FIXES, AND THEY ARE DIFFERENT FIXES.
 *
 *   1. A SECOND HAND. Elapsed time since the last turn, or since the run
 *      started when no turn has landed yet. It moves every second and it is a
 *      measured fact, not decoration.
 *   2. A PULSE. Motion, so a glance is enough. Deliberately on the same
 *      element as the number, so the animation always means "this specific
 *      thing is live" rather than "the page is loading something somewhere".
 *
 * WHAT IS DELIBERATELY NOT HERE: a progress bar, or a percentage of a turn.
 * Nothing knows how far through a model call it is, and inventing a number is
 * how the 0% confidence bar came to be read as progress in the first place.
 * Elapsed time makes no claim about how much is left.
 *
 * `motion-safe:` on every animation, so a reduced-motion preference gets the
 * numbers without the movement.
 */
import { useEffect, useState } from 'react';

/**
 * A clock that ticks only while something is actually in flight.
 *
 * A bare `setInterval` in a component re-renders the whole Bench every second
 * for the entire time an operator leaves the tab open on a finished
 * engagement. Gated on `active`, this costs nothing when nothing is moving.
 */
export function useTicker(active, everyMs = 1000) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [active, everyMs]);

  return now;
}

/**
 * Parse a timestamp the API serialised.
 *
 * THE API SERIALISES NAIVE UTC. Without the marker the browser reads it as
 * local time, which in any negative offset puts the value in the FUTURE and
 * produces a negative elapsed time. Exactly the bug `isInFlight` carries the
 * same guard for, and the reason it is a shared helper here.
 */
export function parseUtc(value) {
  if (!value) return null;
  const at = Date.parse(
    /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`,
  );
  return Number.isFinite(at) ? at : null;
}

/** "44s", "2m 14s", "1h 03m". Compact enough for a 56px rail row. */
export function humanElapsed(ms) {
  if (ms === null || ms === undefined || ms < 0) return null;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

/**
 * How long this run has been on its current turn.
 *
 * Falls back to `started_at`, which is the whole point: `last_turn_at` is null
 * until the first turn LANDS, and the first turn is the one that looked frozen.
 */
export function onTurnFor(run, now = Date.now()) {
  const at = parseUtc(run?.last_turn_at) ?? parseUtc(run?.started_at);
  if (at === null) return null;
  return humanElapsed(now - at);
}

/**
 * A live turn counter with a second hand.
 *
 * `0/40 · 44s` while the first turn runs, `2/40 · 12s` after two have landed.
 * The dot separates a count that steps from a clock that flows, which is the
 * distinction the single number could not make.
 */
export function TurnClock({ run, now, className = '', showCap = true }) {
  if (!run) return null;
  const elapsed = onTurnFor(run, now);
  const live = run.status === 'running';

  return (
    <span
      data-testid="turn-clock"
      title={
        run.turns_used === 0
          ? 'No turn has landed yet. A turn is a model call plus a beacon '
            + 'dispatch, so the first one takes one to three minutes.'
          : `${run.turns_used} of ${run.turn_cap} turns used. The clock is time `
            + 'on the current turn.'
      }
      className={`inline-flex items-center gap-1.5 font-mono ${className}`}
    >
      {live && (
        <span data-testid="live-pulse" className="relative flex h-1.5 w-1.5 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-wasabi-500 opacity-75 motion-safe:animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-wasabi-600" />
        </span>
      )}
      <span>
        {run.turns_used}
        {showCap && <span className="text-slate-400">/{run.turn_cap}</span>}
      </span>
      {live && elapsed && <span className="text-slate-400">&middot; {elapsed}</span>}
    </span>
  );
}

/**
 * The dot that says "this row is the live one".
 *
 * Its own component because three surfaces need it - the rail, the roster and
 * the header chip - and three hand-rolled ping animations drifted apart once
 * already with the three different words for "working".
 */
export function LivePulse({ className = '', tone = 'bg-wasabi-500' }) {
  return (
    <span
      data-testid="live-pulse"
      className={`relative flex h-2 w-2 shrink-0 ${className}`}
      aria-hidden="true"
    >
      <span className={`absolute inline-flex h-full w-full rounded-full ${tone} opacity-75 motion-safe:animate-ping`} />
      <span className={`relative inline-flex h-2 w-2 rounded-full ${tone}`} />
    </span>
  );
}
